/* Measure blank studio margins; retain original photography and all product pixels.
 * Exact approved studio assets are shared by cards, product media, and zoom.
 * Catalogue records and unmatched feature photography are preserved. */
(() => {
  const selector = '.product-card .product__image-first img, .product-card .product__image-second img, .main-product .product-slideshow__item--image img, .gallery__slide--image img, .js-gallery-btn-thumb img, .cart-item__image img';
  const cache = new Map();
  const tracked = new WeakSet();
  const revisions = new WeakMap();
  const boundsByImage = new WeakMap();
  const queue = [];
  let running = 0;
  // Exact catalogue matches only: also cover cart, home features and zoom.
  // Keep the framing normalizer restricted to product media below.
  function replaceApproved(img) {
    const candidates = [img.getAttribute('src'), img.getAttribute('data-src'), img.currentSrc];
    let replacement;
    for (const src of candidates) {
      if (!src) continue;
      try {
        const key = decodeURIComponent(new URL(src, location.href).pathname.split('/').pop());
        const original = key.replace(/_\d+x\d*(?:_crop_[a-z]+)?(?=\.[^.]+$)/i, '');
        replacement = window.MIRAGE_IZOD_STUDIO?.[key] || window.MIRAGE_IZOD_STUDIO?.[original];
        if (replacement) break;
      } catch { /* Ignore non-URL placeholders. */ }
    }
    if (img.closest('.product-card--9066011689098 .product__image-first') && candidates.some(src => /109-AQUA_ALT\.png/i.test(src || ''))) replacement = 'https://cdn.shopify.com/s/files/1/0556/7987/7258/files/luna-aqua-carry-on-retracted-studio.png?v=1793675549';
    if (!replacement) return;
    revisions.set(img, (revisions.get(img) || 0) + 1);
    boundsByImage.delete(img);
    img.classList.remove('mirage-photo-fitted');
    img.classList.remove('mirage-neutral-photo');
    const backgroundOnly = replacement.includes('/izod-studio-bulk-');
    // These bitmaps already contain the final cream surface. Do not tint them twice.
    if (replacement.includes('/izod-studio-duffel-')) img.style.setProperty('mix-blend-mode', 'normal', 'important');
    // White Aero detail pixels blend once into the surrounding cream surface.
    // Keep the contain/letterbox area clear so it cannot multiply cream twice.
    if (replacement.includes('/izod-studio-aero-engraved-')) {
      img.style.setProperty('mix-blend-mode', 'multiply', 'important');
      img.style.setProperty('background-color', 'transparent', 'important');
    }
    if (/\/files\/(?:108|109|1950|1951)-/.test(replacement)) {
      img.style.setProperty('mix-blend-mode', /\.jpe?g(?:\?|$)/i.test(replacement) ? 'multiply' : 'normal', 'important');
      img.style.setProperty('background-color', 'transparent', 'important');
    }
    // Video-matched Aero bitmaps already carry the final cream studio backdrop.
    if (replacement.includes('/izod-studio-aero-video-')) {
      img.style.setProperty('mix-blend-mode', 'normal', 'important');
      img.style.setProperty('background-color', 'transparent', 'important');
    }
    // Refined plaques: retain transparent cutouts; match the Aqua white backdrop to cream.
    if (replacement.includes('/izod-studio-aero-plaque2-')) {
      img.style.setProperty('mix-blend-mode', replacement.includes('108-aqua_alt5') ? 'multiply' : 'normal', 'important');
      img.style.setProperty('background-color', 'transparent', 'important');
    }
    if (replacement.includes('/izod-studio-aero-flush-')) {
      img.style.setProperty('mix-blend-mode', 'normal', 'important');
      img.style.setProperty('background-color', 'transparent', 'important');
    }
    if (replacement.includes('/izod-studio-luna-video-')) {
      img.style.setProperty('mix-blend-mode', 'normal', 'important');
      img.style.setProperty('background-color', 'transparent', 'important');
    }
    img.classList.toggle('mirage-approved-studio', !backgroundOnly);
    img.classList.toggle('mirage-bulk-corrected', backgroundOnly);
    img.removeAttribute('srcset');
    img.removeAttribute('data-srcset');
    if (img.hasAttribute('data-src')) img.setAttribute('data-src', replacement);
    img.closest('picture')?.querySelectorAll('source').forEach(source => {
      source.removeAttribute('srcset');
      source.removeAttribute('data-srcset');
    });
    if (img.getAttribute('src') !== replacement) img.setAttribute('src', replacement);
    if ((backgroundOnly || replacement.includes('/izod-studio-duffel-')) && !img.closest('.gallery__slide--image')) {
      // Small cards load small files; the zoom viewer retains the untouched resolution.
      const small = replacement.replace('.webp', '-720.webp');
      const medium = replacement.replace('.webp', '-1440.webp');
      const width = window.MIRAGE_BULK_WIDTHS?.[new URL(replacement, location.href).pathname.split('/').pop()];
      const sources = [];
      if (width > 720) sources.push(`${small} 720w`);
      if (width > 1440) sources.push(`${medium} 1440w`);
      if (width) sources.push(`${replacement} ${width}w`);
      if (sources.length) img.setAttribute('srcset', sources.join(', '));
      img.setAttribute('sizes', img.closest('.product-card') ? '(max-width: 768px) 50vw, 25vw' : '(max-width: 768px) 100vw, 50vw');
    }
  }
  function measure(src) {
    const key = new URL(src, location.href).pathname.split('/').pop();
    if (window.MIRAGE_PHOTO_BOUNDS && Object.hasOwn(window.MIRAGE_PHOTO_BOUNDS, key)) return Promise.resolve(window.MIRAGE_PHOTO_BOUNDS[key]);
    if (cache.has(src)) return cache.get(src);
    const job = new Promise(resolve => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ratio = probe.naturalWidth / probe.naturalHeight;
          canvas.width = Math.round(200 * Math.min(ratio, 1));
          canvas.height = Math.round(200 / Math.max(ratio, 1));
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(probe, 0, 0, canvas.width, canvas.height);
          const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const w = canvas.width, h = canvas.height;
          const blank = (x, y) => {
            const i = (y * w + x) * 4;
            return data[i + 3] < 25 || (Math.min(data[i], data[i + 1], data[i + 2]) > 225 && Math.max(data[i], data[i + 1], data[i + 2]) - Math.min(data[i], data[i + 1], data[i + 2]) < 17);
          };
          // Only normalize neutral studio photography, never a lifestyle scene.
          let edges = 0, light = 0;
          for (let x = 0; x < w; x++) { edges += 2; light += blank(x, 0) + blank(x, h - 1); }
          for (let y = 0; y < h; y++) { edges += 2; light += blank(0, y) + blank(w - 1, y); }
          if (light / edges < .985) return resolve(null);
          let x0 = w, x1 = 0, y0 = h, y1 = 0, ground = 0;
          for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            if (!blank(x, y)) { x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
            const i = (y * w + x) * 4;
            if (data[i + 3] > 200 && Math.min(data[i], data[i + 1], data[i + 2]) < 145) ground = Math.max(ground, y);
          }
          if (x1 <= x0 || y1 <= y0) return resolve(null);
          // Preserve a generous margin for pale edges, wheels, handles and shadows.
          x0 = Math.max(0, x0 - 5); y0 = Math.max(0, y0 - 5);
          x1 = Math.min(w, x1 + 6); y1 = Math.min(h, y1 + 6);
          resolve({ width: (x1 - x0) / w, height: (y1 - y0) / h, cx: (x0 + x1) / (2 * w), cy: (y0 + y1) / (2 * h), ground: ground / h, ratio });
        } catch { resolve(null); }
      };
      probe.onerror = () => resolve(null);
      const sample = new URL(src, location.href);
      if (sample.pathname.includes('/files/')) sample.searchParams.set('width', '240');
      probe.src = sample.href;
    });
    cache.set(src, job);
    return job;
  }
  function fit(img) {
    // Full-resolution zoom and cart photos must never be enlarged or cropped.
    if (img.closest('.gallery__slide--image, .js-gallery-btn-thumb, .cart-item__image')) return;
    const b = boundsByImage.get(img);
    if (!b || !img.clientWidth || !img.clientHeight) return;
    const w = img.clientWidth, h = img.clientHeight;
    const iw = Math.min(w, h * b.ratio), ih = iw / b.ratio;
    const fill = .90;
    const scale = Math.min(2.5, w * fill / (iw * b.width), h * fill / (ih * b.height));
    img.style.setProperty('--photo-scale', scale.toFixed(4));
    img.style.setProperty('--photo-x', `${((.5 - b.cx) * iw * scale).toFixed(2)}px`);
    img.style.setProperty('--photo-y', `${((.5 - b.cy) * ih * scale).toFixed(2)}px`);
    img.parentElement.style.setProperty('--photo-ground', `${(img.offsetTop + h / 2 + ih * ((b.ground || b.cy + b.height / 2) - b.cy) * scale - h * .01).toFixed(2)}px`);
    img.classList.add('mirage-photo-fitted');
  }
  function drain() {
    while (running < 3 && queue.length) {
      const img = queue.shift();
      const src = img.currentSrc || img.src;
      if (!src || !img.isConnected) continue;
      const studioKey = new URL(src, location.href).pathname.split('/').pop();
      const studioUrl = window.MIRAGE_IZOD_STUDIO?.[studioKey];
      if (studioUrl) {
        img.classList.remove('mirage-photo-fitted');
        boundsByImage.delete(img);
        img.removeAttribute('srcset');
        img.src = studioUrl;
        continue;
      }
      if (src.includes('/izod-studio-') && !src.includes('/izod-studio-bulk-')) {
        img.classList.remove('mirage-photo-fitted');
        boundsByImage.delete(img);
        continue;
      }
      if (window.MIRAGE_ISLA_STUDIO_URL && /\/1804-BLACK_1\.jpg/i.test(src)) {
        img.classList.remove('mirage-photo-fitted');
        boundsByImage.delete(img);
        img.removeAttribute('srcset');
        img.src = window.MIRAGE_ISLA_STUDIO_URL;
        continue;
      }
      if (src.includes('isla-studio-grounded-v1')) {
        img.classList.remove('mirage-photo-fitted');
        boundsByImage.delete(img);
        continue;
      }
      // Inspect the neutral backdrop in every viewer, but keep zoom/cart sizing intact.
      const revision = (revisions.get(img) || 0) + 1;
      revisions.set(img, revision);
      img.classList.remove('mirage-photo-fitted');
      img.classList.remove('mirage-neutral-photo');
      boundsByImage.delete(img);
      running++;
      measure(src).then(b => {
        if (revisions.get(img) !== revision || (img.currentSrc || img.src) !== src) return;
        img.classList.toggle('mirage-neutral-photo', Boolean(b));
        if (img.closest('.gallery__slide--image, .js-gallery-btn-thumb, .cart-item__image')) return;
        const frame = img.parentElement;
        frame.classList.add('mirage-photo-frame');
        frame.dataset.studio = String(Boolean(b));
        if (b) { boundsByImage.set(img, b); fit(img); }
      }).finally(() => { running--; drain(); });
    }
  }
  const resize = new ResizeObserver(entries => entries.forEach(({ target }) => fit(target)));
  const visible = new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      visible.unobserve(target);
      queue.push(target); drain();
    });
  }, { rootMargin: '300px' });
  function scan() {
    document.querySelectorAll('img').forEach(replaceApproved);
    document.querySelectorAll(selector).forEach(img => {
      if (tracked.has(img)) return;
      tracked.add(img);
      resize.observe(img);
      visible.observe(img);
      img.addEventListener('load', () => { queue.push(img); drain(); });
    });
  }
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  scan();
})();
